import { Component, Input, OnInit } from '@angular/core';
import { widgetDefinitions } from '../../widgets-definitions';
import { DestroyBase } from 'src/app/components/common/ng-destroy-base';
import { ReportDesignerHostComponent } from '../../report-designer-host/report-designer-host.component';
import { ReportDesignerWidgetDefinition } from '../../models/report-designer-widget-definition';
import { ReportDesignerWidgetCategoryDefinition } from '../../models/report-designer-widget-category-definition';
import { EventBusService } from '../../../../services/event-bus.service';
import { ReportDesignerStorageService } from '../../services/report-designer-storage.service';
import { GuidService } from '../../../project-valuation/services/guid.service';
import { ObjectUtil } from '../../../../components/common/object-util/object-util';
import { ReportDesignerPage } from '../../models/report-designer-page';
import { ReportDesignerWidgetConfig } from '../../models/report-designer-widget-config';
import { ReportDesignerDocumentComponent } from '../report-designer-document/report-designer-document.component';

/**
 * Widget list component
 */
@Component({
  selector: 'report-designer-widget-list',
  templateUrl: './report-designer-widget-list.component.html',
  styleUrls: ['./report-designer-widget-list.component.scss']
})
export class ReportDesignerWidgetListComponent extends DestroyBase implements OnInit {
  /**
   * RD host component
   */
  @Input() public main: ReportDesignerHostComponent;

  /**
   *
   * Constructor
   * @param rdStorage
   * @param eventBus
   * @param guidService
   */
  constructor(private rdStorage: ReportDesignerStorageService,
    private eventBus: EventBusService,
    private guidService: GuidService) {
    super();
  }

  /**
   * Widget definitions
   */
  public widgetsDefinitions: ReportDesignerWidgetCategoryDefinition[] = [];

  /**
   * Widget list
   */
  private widgetList: ReportDesignerWidgetDefinition[] = [];

  /**
   * initializes the component
   */
  ngOnInit(): void {
    this.widgetsDefinitions = widgetDefinitions;
    this.initWidgetList();

    this.registerEvents();
  }

  /**
   * Initializes the widget list
   */
  private initWidgetList() {
    for (let i = 0; i < this.widgetsDefinitions.length; ++i) {
      const def = this.widgetsDefinitions[i];

      def.id = "WidgetGroup" + i;
      if (def.items && def.items.length > 0) {
        for (const widget of def.items) {
          this.checkWidgeConfigUndefined(widget);
          this.checkDuplicateWidgetId(def, widget);
          widget.encoded = false;
          this.widgetList.push(widget);
        }
        def.items.sort((a, b) => { return a.WidgetNameEnglish < b.WidgetNameEnglish ? -1 : 1; });
      }
    }
  }

  /**
   * Checks for duplicate widget IDs
   * @param def
   * @param widget
   */
  private checkDuplicateWidgetId(def: ReportDesignerWidgetCategoryDefinition, widget: ReportDesignerWidgetDefinition) {
    if (def.items.find((w) => { return w !== widget && w.config.widgetId === widget.config.widgetId; })) {
      alert('Configuration error: Duplicate config.widgetId: ' + widget.config.widgetId);
    }
  }

  /**
   * Checks for undefined widget configs
   * @param widget
   */
  private checkWidgeConfigUndefined(widget: ReportDesignerWidgetDefinition) {
    if (typeof widget.config === "undefined") {
      alert('Configuration error: widget config is undefined: ' + widget.WidgetNameEnglish);
    }
  }

  /**
   * Update flag for each widget if it is already used in report
   */
  public updateUsedWidgets(): void {
    for (const widgetListEntry of this.widgetList) {
      //Process all available widgets
      widgetListEntry.alreadyExists = false;
      const document = this.main.getActiveDocument();
      if (document?.data?.pages) {
        const activePages = document.data.pages;
        if (activePages?.length > 0) {
          this.processPages(activePages, widgetListEntry);
        }
      }
    }
  }

  /**
   * Process all pages
   * @param activePages
   * @param widgetListEntry
   */
  private processPages(activePages: ReportDesignerPage[], widgetListEntry: ReportDesignerWidgetDefinition): void {
    for (const page of activePages) {
      //Process all pages
      if (page.widgets && page.widgets.length > 0) {
        this.processWidgetsOnPage(page, widgetListEntry);
      }
    }
  }

  /**
   * Process all widgets on a page and update their usage state
   * @param page
   * @param widgetListEntry
   */
  private processWidgetsOnPage(page: ReportDesignerPage, widgetListEntry: ReportDesignerWidgetDefinition): void {
    for (const widget of page.widgets) {
      //Process all widgets on the page
      if (widget.widgetId == widgetListEntry.config.widgetId) {
        widgetListEntry.alreadyExists = true;

        //The following might change on global widget config level and therefore might need to update
        if (typeof (widget.minSizeX) !== "undefined") widget.minSizeX = widgetListEntry.config.minSizeX;
        if (typeof (widget.minSizeY) !== "undefined") widget.minSizeY = widgetListEntry.config.minSizeY;
        if (typeof (widget.maxSizeX) !== "undefined") widget.maxSizeX = widgetListEntry.config.maxSizeX;
        if (typeof (widget.maxSizeY) !== "undefined") widget.maxSizeY = widgetListEntry.config.maxSizeY;
      }
    }
  }

  /**
   * Setup a widget's config
   * @param config
   * @param isPagePortrait
   */
  public setupWidgetConfig(config: ReportDesignerWidgetConfig, isPagePortrait: boolean): void {
    //we are doing changes on "deep copy" of widget definitions
    if (!isPagePortrait) {
      //for landscape widgets, copy values from "L" properties into "normal" gridster properties (that by default are used for portrait widgets)
      config.sizeX = config.sizeXL;
      config.sizeY = config.sizeYL;
      config.minSizeX = config.minSizeXL;
      config.minSizeY = config.minSizeYL;
      config.maxSizeX = config.maxSizeXL;
      config.maxSizeY = config.maxSizeYL;
    }

    //delete landscape sizes, so they are not stored to database (no need for it)
    delete config.sizeXL;
    delete config.sizeYL;
    delete config.minSizeXL;
    delete config.minSizeYL;
    delete config.maxSizeXL;
    delete config.maxSizeYL;

    //also delete possibly undefined values
    if (typeof (config.minSizeX) === "undefined") delete config.minSizeX;
    if (typeof (config.minSizeY) === "undefined") delete config.minSizeY;
    if (typeof (config.maxSizeX) === "undefined") delete config.maxSizeX;
    if (typeof (config.maxSizeY) === "undefined") delete config.maxSizeY;
  }

  /**
    * Registers any required events. Please don't forget to unsubscibe!
    */
  private registerEvents(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('widgetsChanged', () => {
      this.updateUsedWidgets()
    }));
  }

  /**
   * Raised when widget's pin is clicked in list. Prepares widget to be dropped on report
   * @param id Widget ID
   * @param $event Event
   */
  public addWidget(id: number, $event): void {
    if ($event) {
      $event.originalEvent.stopPropagation();
      $event.originalEvent.preventDefault();
    }

    //when clicking on any widget button, turn off "widget drop mode"
    this.rdStorage.setWidgetTargetDropMode(false, null);
    
    if (id != 0 && this.main.activePage != null) {
      this.addWidgetInner(id);
    }
    else {
      this.openConfirmationDialog('widgetNotImplemented')
    }
  }

  /**
   * Add widget (continued)
   * @param id
   * @returns
   */
  public addWidgetInner(id) {
    const dropModeOnStart = this.rdStorage.getWidgetTargetDropMode();
    const dropModeOptionsOnStart = this.rdStorage.getWidgetTargetDropModeOptions();
    let widgetCantBeAdded = false;
    const widgetDefinition = this.widgetList.find((widgetDefinition) => { return widgetDefinition.config.widgetId === id; });
    if (widgetDefinition) {
      const value = ObjectUtil.deepCopy(widgetDefinition) as ReportDesignerWidgetDefinition;

      const activeDocument = this.main.getActiveDocument();
      const { alreadyExists, widgetGuid, widgetPage } = this.checkExistingWidget(activeDocument, value);

      if (alreadyExists && !value.config.multipleInstances) {
        this.eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            setTimeout(() => {
              this.eventBus.broadcastEvent("scrollToWidget", { widgetGuid, widgetPage });
            }, 200);
          }
        }, 'addWidgetOnce');
        return;
      }

      widgetCantBeAdded = this.handleMultiLayout(alreadyExists, value, activeDocument, widgetCantBeAdded);

      widgetCantBeAdded = this.handleMultiCalculation(alreadyExists, value, activeDocument, widgetCantBeAdded);

      if (!widgetCantBeAdded) {

        this.addWidgetToDocument(value, dropModeOnStart, dropModeOptionsOnStart);
      }
    }
  }

  /**
   * Adds a widget to the document
   * @param value
   * @param dropModeOnStart
   * @param dropModeOptionsOnStart
   */
  private addWidgetToDocument(value: ReportDesignerWidgetDefinition, dropModeOnStart: boolean, dropModeOptionsOnStart: ReportDesignerWidgetConfig) {
    const config = ObjectUtil.deepCopy(value.config) as ReportDesignerWidgetConfig; //Deep copy
    config.widgetSettings.id = this.guidService.generate();
    //setup widget config (portrait/landscape)
    const isPagePortrait = this.main.activePage.options.isPortrait;
    const isWidgetPortrait = config.supportsPortrait;
    const isWidgetLandscape = config.supportsLandscape;

    if ((isPagePortrait && isWidgetPortrait) || (!isPagePortrait && isWidgetLandscape)) {
      this.setupWidgetConfig(config, isPagePortrait);

      if (dropModeOnStart && dropModeOptionsOnStart && dropModeOptionsOnStart.widgetId === config.widgetId) {
        //we clicked on same widget => just turn drop mode off
        this.rdStorage.setWidgetTargetDropMode(false, null);
      } else {
        this.rdStorage.setWidgetTargetDropMode(true, config);
      }

      //widget will be added in "drop mode"
    }
    else {
      this.rdStorage.setWidgetTargetDropMode(false, null);
      this.openConfirmationDialog('widgetNotAvailableForPageFormat');
    }

    this.updateUsedWidgets();
  }

  /**
   * Handle calculation restrictions
   * @param alreadyExists
   * @param value
   * @param activeDocument
   * @param widgetCantBeAdded
   * @returns
   */
  private handleMultiCalculation(alreadyExists: boolean, value: ReportDesignerWidgetDefinition, activeDocument: ReportDesignerDocumentComponent, widgetCantBeAdded: boolean) {
    if (alreadyExists && value.config.multipleInstances && value.config.oneInstancePerCalculation) {
      const usedCalculations = this.getUsedCalculations(activeDocument, value);

      const availableCalculations = value.config.widgetSettings.availableCalculations;
      const MAX_COUNT = typeof (availableCalculations) !== 'undefined' ? availableCalculations.length : value.config.widgetSettings.maxCalculations;

      if (usedCalculations.length >= MAX_COUNT) {
        widgetCantBeAdded = true;
        this.openConfirmationDialog('addWidgetOncePerLayout');
      }
    }

    return widgetCantBeAdded;
  }

  /**
   * Handle layout restrictions
   * @param alreadyExists
   * @param value
   * @param activeDocument
   * @param widgetCantBeAdded
   * @returns
   */
  private handleMultiLayout(alreadyExists: boolean, value: ReportDesignerWidgetDefinition, activeDocument: ReportDesignerDocumentComponent, widgetCantBeAdded: boolean) {
    if (alreadyExists && value.config.multipleInstances && value.config.oneInstancePerLayout) {
      const usedLayouts = this.getUsedLayouts(activeDocument, value);

      const availableLayouts = value.config.widgetSettings.availableLayouts;
      const MAX_COUNT = typeof (availableLayouts) !== 'undefined' ? availableLayouts.length : value.config.widgetSettings.maxLayouts;

      if (usedLayouts.length >= MAX_COUNT) {
        widgetCantBeAdded = true;
        this.openConfirmationDialog('addWidgetOncePerLayout');
      } else if (availableLayouts) {
        //assign next unused layout
        const choosableLayouts = availableLayouts.filter((el) => !usedLayouts.includes(el));
        value.config.widgetSettings.currentLayout = choosableLayouts[0];
      }
    }
    return widgetCantBeAdded;
  }

  /**
   * Checks if the widget is already inserted
   * @param activeDocument
   * @param value
   * @returns
   */
  private checkExistingWidget(activeDocument: ReportDesignerDocumentComponent, value: ReportDesignerWidgetDefinition): { alreadyExists: boolean, widgetGuid: string, widgetPage: ReportDesignerPage } {
    let alreadyExists = false;

    let widgetGuid = null;
    let widgetPage = null;
    for (const page of activeDocument.data.pages) {
      for (const widget of page.widgets) {
        if (widget.widgetId == value.config.widgetId) {
          alreadyExists = true;
          widgetGuid = widget.widgetSettings.id;
          widgetPage = page;
        }
      }
    }
    return { alreadyExists, widgetGuid, widgetPage };
  }

  /**
   *
   * Gets the used layouts
   * @param activeDocument
   * @param value
   * @returns
   */
  private getUsedLayouts(activeDocument: ReportDesignerDocumentComponent, value: ReportDesignerWidgetDefinition): string[] {
    const usedLayouts: string[] = [];
    for (const page of activeDocument.data.pages) {
      for (const widget of page.widgets) {
        if (widget.widgetId == value.config.widgetId) {
          if (usedLayouts.indexOf(widget.widgetSettings.currentLayout) < 0) {
            usedLayouts.push(widget.widgetSettings.currentLayout);
          }
        }
      }
    }
    return usedLayouts;
  }

  /**
   * Gets the used calculations
   * @param activeDocument
   * @param value
   * @returns
   */
  private getUsedCalculations(activeDocument: ReportDesignerDocumentComponent, value: ReportDesignerWidgetDefinition): number[] {
    const usedCalculations: number[] = [];
    for (const page of activeDocument.data.pages) {
      for (const widget of page.widgets) {
        if (widget.widgetId == value.config.widgetId) {
          if (usedCalculations.indexOf(widget.widgetSettings.currentCalculation) < 0) {
            usedCalculations.push(widget.widgetSettings.currentCalculation);
          }
        }
      }
    }
    return usedCalculations;
  }

  /**
   * Open the confirm dialog to show a message
   * @param message
   */
  private openConfirmationDialog(message: string): void {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        //Nothing
      }
    }, message);
  }
}


